import {Color, BackgroundColor, Theme} from '@adobe/leonardo-contrast-colors';
import type {
  CssColor as _CssColor,
  ContrastColor,
} from '@adobe/leonardo-contrast-colors';
import chroma from 'chroma-js';

export type CssColor = _CssColor;
export type Background = CssColor | 'light' | 'dark';

export type BrandColor = {
  key: string;
  value: CssColor;
};
export type Palette = Record<string, CssColor>;

const PALETTE_STEPS_LIGHT = [
  1, 1.06, 1.13, 1.21, 1.3, 1.38, 1.48, 1.59, 1.71, 1.84, 1.96, 2.12, 2.3, 2.49,
  2.68, 2.92, 3.15, 3.45, 3.74, 4.12, 4.48, 4.88, 5.41, 5.92, 6.48, 7.11, 7.81,
  8.59, 9.29, 10.21, 11.2, 12.08, 13.2, 14.16, 15.13, 16.1, 17.22, 17.93, 18.88,
  19.91, 20,
];

const PALETTE_STEPS_DARK = [
  1.0, 1.375, 1.75, 2.125, 2.5, 2.875, 3.25, 3.625, 4.0, 4.375, 4.75, 5.125,
  5.5, 5.875, 6.25, 6.625, 7, 7.417, 7.833, 8.25, 8.667, 9.083, 9.5, 9.917,
  10.333, 10.75, 11.167, 11.583, 12.0, 12.417, 12.833, 13.25, 13.667, 14.083,
  14.5, 14.917, 15.333, 15.75, 16.167, 16.583, 17.0,
];

const STATIC_COLORS: BrandColor[] = [
  {key: 'error', value: '#d24b4e'},
  {key: 'slate-blue', value: '#374a67'},
  {key: 'aqua-lime', value: '#5cffad'},
  {key: 'blood-Orange', value: '#ff6401'},
  {key: 'pale-silver', value: '#eff3f3'},
  {key: 'success', value: '#339970'},
  {key: 'warning', value: '#e07315'},
  {key: 'info', value: '#3c64b9'},
];

const DEFAULT_BACKGROUND_COLOR: CssColor = '#000000';

const BACKGROUND_PRESETS = {
  light: {
    lightness: 100,
    constrast: 1,
  },
  dark: {
    lightness: 10,
    constrast: 1,
  },
} as const;

const getBackgroundColor = (background: Background) => {
  if (background === 'light' || background === 'dark') {
    return DEFAULT_BACKGROUND_COLOR;
  }
  return background;
};

const getBackgroundLightness = (background: Background) => {
  if (background === 'light') {
    return BACKGROUND_PRESETS.light.lightness;
  }
  if (background === 'dark') {
    return BACKGROUND_PRESETS.dark.lightness;
  }
  return Math.round(chroma(background).oklch()[0] * 100);
};

const getBackgroundContrast = (background: Background, lightness: number) => {
  if (background === 'light') {
    return BACKGROUND_PRESETS.light.constrast;
  }
  if (background === 'dark') {
    return BACKGROUND_PRESETS.dark.constrast;
  }
  return 2 - Math.max(1, lightness) / 100;
};

const formatPalette = (palette: ContrastColor['values'], key: string) => {
  const formattedPalette = palette.reduce(
    (acc, color) => {
      acc.palette[`--palettes-${key}-${acc.shade}`] = color.value;
      acc.shade += 25;
      return acc;
    },
    {palette: {}, shade: 0}
  );
  return formattedPalette.palette;
};

export const generatePalette = (
  brandColors: BrandColor[],
  backgroundColor: Background = 'light'
) => {
  const backgroundColorInput = getBackgroundColor(backgroundColor);
  const backgroundLightness = getBackgroundLightness(backgroundColor);
  const contrast = getBackgroundContrast(backgroundColor, backgroundLightness);
  const ratios =
    backgroundColor === 'dark' ? PALETTE_STEPS_DARK : PALETTE_STEPS_LIGHT;

  const backgroundColorOutput = new BackgroundColor({
    name: 'neutral',
    colorKeys: [backgroundColorInput],
    ratios: ratios,
    colorspace: 'OKLCH',
    smooth: true,
  });

  const themeColors = [...STATIC_COLORS, ...brandColors].map(
    color =>
      new Color({
        name: color.key,
        colorKeys: [color.value],
        ratios: ratios,
        colorspace: 'OKLCH',
        smooth: true,
      })
  );

  const theme = new Theme({
    colors: [backgroundColorOutput, ...themeColors],
    backgroundColor: backgroundColorOutput,
    lightness: backgroundLightness,
    contrast: contrast,
    saturation: 100,
    output: 'HEX',
    formula: 'wcag2',
  });

  const [generatedBackground, ...brandPalettes] = theme.contrastColors;

  const formattedPalettes = brandPalettes.map(palette =>
    formatPalette(palette.values, palette.name)
  );

  return formattedPalettes.reduce((r, c) => Object.assign(r, c), {
    '--palettes-background-0': generatedBackground.background,
  });
};

export const formatStyleString = (palette: Record<string, CssColor>) => {
  return Object.entries(palette)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n');
};
